import { useEffect, useRef, useState } from "react";
import { CompressedTexture, Material, Mesh, Object3D, Texture } from "three";
import modelAssets from "url:../models/exported/*";
import modelAssetsHigh from "url:../models/exported/high/*";
import modelAssetsMed from "url:../models/exported/medium/*";
import modelAssetsLow from "url:../models/exported/low/*";
import { USE_BASIS_TEXTURES } from "../App";
import { FocusedObject } from "../contexts/AppContext";
import DeviceGradeDetection, {
  DeviceGrade,
  NetworkEffectiveType,
} from "./DeviceGradeDetection";

export const setTextureProperties = (child: Object3D, properties) => {
  child.traverse((child: Object3D) => {
    if (child instanceof Mesh) {
      for (const key of Object.keys(child.material)) {
        const value = child.material[key];
        if (value && typeof value === "object" && "minFilter" in value) {
          Object.keys(properties).forEach((propertyKey) => {
            value[propertyKey] = properties[propertyKey];
          });
        }
      }
    }
  });
};

export const disposeMaterial = (material: Material) => {
  if (material.uniforms) {
    Object.keys(material.uniforms).forEach((key) => {
      if (
        material.uniforms[key].value instanceof Texture ||
        material.uniforms[key].value instanceof CompressedTexture
      ) {
        material.uniforms[key].value.dispose();
        delete material.uniforms[key].value;
      }
    });
  }

  for (const key of Object.keys(material)) {
    const value = material[key];

    if (Array.isArray(value)) {
      value.forEach((valueChild) => {
        if (
          valueChild &&
          typeof valueChild === "object" &&
          "minFilter" in value
        ) {
          valueChild.dispose();
          delete value[valueChild];
        }
      });
    } else if (value && typeof value === "object" && "minFilter" in value) {
      value.dispose();
      delete material[value];
    }
  }
  material.dispose();
};

export const disposeComplete = (parent: any) => {
  parent.traverse((object: Mesh) => {
    if (object.geometry) {
      object.geometry.dispose();
    }
    if (object.material) {
      if (Array.isArray(object.material)) {
        object.material.forEach((material) => {
          disposeMaterial(material);
        });
      } else {
        disposeMaterial(object.material);
      }
    }
  });

  while (parent.children.length) {
    parent.remove(parent.children[0]);
  }
};

export enum TextureSize {
  "High",
  "Med",
  "Low",
}

const LO_RESOLUTION_MAX = 375 * 620;
const MED_RESOLUTION_MAX = 1400 * 700;

export const getTextureSize: () => Promise<TextureSize> = async () => {
  if (window.location.search.indexOf("tex-LO") > -1) {
    return TextureSize.Low;
  }

  if (window.location.search.indexOf("tex-MED") > -1) {
    return TextureSize.Med;
  }

  if (window.location.search.indexOf("tex-HI") > -1) {
    return TextureSize.High;
  }

  const gl = (window as any).webGlView.context;
  const maxTextureSize = parseInt(gl.getParameter(gl.MAX_TEXTURE_SIZE));
  const resolution = window.innerWidth * window.innerHeight;

  console.info(`gl.MAX_TEXTURE_SIZE: ${gl.getParameter(gl.MAX_TEXTURE_SIZE)}`);
  console.info(
    `gl.MAX_COMBINED_TEXTURE_IMAGE_UNITS: ${gl.getParameter(
      gl.MAX_COMBINED_TEXTURE_IMAGE_UNITS
    )}`
  );

  const networkEffectiveType =
    await DeviceGradeDetection.getNetworkEffectiveType();

  if (resolution <= LO_RESOLUTION_MAX) {
    console.info("Use LO resolution");
    return TextureSize.Low;
  }

  if (
    (networkEffectiveType === NetworkEffectiveType.Slow2G ||
      networkEffectiveType === NetworkEffectiveType["2G"] ||
      networkEffectiveType === NetworkEffectiveType["3G"]) &&
    resolution <= MED_RESOLUTION_MAX
  ) {
    console.info("Use LO resolution (2g / 3g connection)");
    return TextureSize.Low;
  }

  if (maxTextureSize < 4000) {
    console.info("Use LO resolution (max texture size)");
    return TextureSize.Low;
  }

  if (resolution <= MED_RESOLUTION_MAX) {
    console.info("Use MED resolution");
    return TextureSize.Med;
  }

  if (maxTextureSize < 8000) {
    console.info("Use MED resolution (max texture size)");
    return TextureSize.Med;
  }

  if (DeviceGradeDetection.isSafari) {
    console.info("Use MED resolution (is Safari)");
    return TextureSize.Med;
  }

  if (DeviceGradeDetection.deviceGrade < DeviceGrade.High) {
    console.info("Use MED resolution (is lower grade device)");
    return TextureSize.Med;
  }

  if (
    networkEffectiveType === NetworkEffectiveType.Slow2G ||
    networkEffectiveType === NetworkEffectiveType["2G"] ||
    networkEffectiveType === NetworkEffectiveType["3G"]
  ) {
    console.info("Use MED resolution (2g / 3g connection)");
    return TextureSize.Med;
  }
  console.info("Use HI resolution");
  return TextureSize.High;
};

export const loaderUrlModifierForTextures = (url) => {
  const filename = url.substring(url.lastIndexOf("/") + 1).split("?")[0];
  const parts = filename.split(".");
  const name = parts[0];
  let ext = parts[parts.length - 1];
  const originalExt = ext;

  let modifiedUrl;
  if (["jpg", "jpeg", "png"].indexOf(ext) > -1) {
    if (USE_BASIS_TEXTURES) {
      ext = "ktx2";
    }
    switch ((window as any).textureSize) {
      case TextureSize.High:
        modifiedUrl = modelAssetsHigh[`${name}.${ext}`];
        break;
      case TextureSize.Med:
        modifiedUrl = modelAssetsMed[`${name}.${ext}`];
        break;
      case TextureSize.Low:
        modifiedUrl = modelAssetsLow[`${name}.${ext}`];
        break;
    }
  } else {
    modifiedUrl = modelAssets[`${name}.${ext}`];
  }

  if (!modifiedUrl) return url;
  return modifiedUrl;
};

export const useFollowObject = (toFollow: FocusedObject) => {
  // no idea why but the animation frame handle doesn't work it just still runs even after cleanup, so we have to set a flag
  const shouldRun = useRef(false);
  const animationFrameHandle = useRef<any>();
  const [position, setPosition] = useState<{ x: number; y: number }>({
    x: 0,
    y: 0,
  });

  const getPosition = () => {
    const { x, y } = toFollow.screenSpacePosition.position;
    setPosition({ x, y });

    if (shouldRun.current) {
      animationFrameHandle.current = requestAnimationFrame(getPosition);
    }
  };

  useEffect(() => {
    if (toFollow) {
      shouldRun.current = true;
      cancelAnimationFrame(animationFrameHandle.current);
      animationFrameHandle.current = requestAnimationFrame(getPosition);
    }

    return () => {
      shouldRun.current = false;
      cancelAnimationFrame(animationFrameHandle.current);
    };
  }, [toFollow]);

  return position;
};

class RandomDict {
  private numbers: number[] = [];
  private cursor = 0;

  constructor(count = 999999) {
    for (let i = 0; i < count; i++) {
      this.numbers.push(Math.random());
    }
  }

  public get random(): number {
    this.cursor++;
    return this.numbers[this.cursor % this.numbers.length];
  }
}

export const randomDict = new RandomDict();
